import { useNavigate } from 'react-router-dom'

import type { ThemeId } from '../../theme/ThemeContext'
import { AsyncPullLoadingIndicator } from '../async/AsyncPullLoadingIndicator'
import { PageAsyncErrorState } from '../async/PageAsyncErrorState'
import { PageCenteredPlaceholder } from '../async/PageCenteredPlaceholder'
import { ChromePrimaryButton } from '../ChromeActions'

function AccessDeniedIllustration({ angular }: { angular: boolean }) {
  const rx = angular ? 0 : 8
  return (
    <svg
      className="mx-auto h-[4.5rem] w-[4.5rem] shrink-0 text-ink-soft/45"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="14"
        y="28"
        width="44"
        height="32"
        rx={rx}
        className="stroke-current"
        strokeWidth={angular ? 2 : 1.75}
        strokeOpacity={0.55}
      />
      <path
        d="M36 18v14M28 18h16a4 4 0 0 1 4 4v2H24v-2a4 4 0 0 1 4-4Z"
        className="stroke-current"
        strokeWidth={angular ? 2 : 1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity={0.75}
      />
      <circle cx="36" cy="44" r="3" className="fill-current" fillOpacity={0.5} />
    </svg>
  )
}

type LoadingProps = {
  theme: ThemeId
  label?: string
  className?: string
}

/** 权限摘要加载：与图库/书架首屏 {@link AsyncPullLoadingIndicator} 一致 */
export function ThemedPermissionLoadingState({
  theme,
  label,
  className,
}: LoadingProps) {
  const angular = theme === 'pixel' || theme === 'geek'
  const terminalish = theme === 'geek'
  return (
    <AsyncPullLoadingIndicator
      angular={angular}
      label={label ?? (terminalish ? 'loading_perms…' : '正在加载权限…')}
      className={className}
    />
  )
}

type DeniedProps = {
  theme: ThemeId
  /** 无可用页时的兜底文案 */
  detail?: string
  onGoBack?: () => void
  backLabel?: string
}

/** 无权访问占位：复用 {@link PageAsyncErrorState} 排版 + Chrome 主按钮 */
export function ThemedAccessDeniedState({
  theme,
  detail,
  onGoBack,
  backLabel,
}: DeniedProps) {
  const angular = theme === 'pixel' || theme === 'geek'
  const terminalish = theme === 'geek'
  const title = terminalish ? 'access_denied' : '无权访问'
  const body =
    detail ??
    (terminalish
      ? 'current_account_lacks_permission'
      : '当前账号没有访问此功能的权限。如需开通，请联系管理员。')

  if (!onGoBack) {
    return (
      <PageAsyncErrorState
        theme={theme}
        title={title}
        detail={body}
        illustration={<AccessDeniedIllustration angular={angular} />}
      />
    )
  }

  return (
    <PageCenteredPlaceholder role="alert">
      <AccessDeniedIllustration angular={angular} />
      <h2
        className={[
          'typo-lib-section font-semibold text-ink',
          terminalish ? 'font-mono tracking-tight' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {title}
      </h2>
      <p className="text-pretty text-sm leading-relaxed text-ink-soft">{body}</p>
      <ChromePrimaryButton density="compact" type="button" onClick={onGoBack}>
        {backLabel ?? (terminalish ? 'go_back' : '返回可用页面')}
      </ChromePrimaryButton>
    </PageCenteredPlaceholder>
  )
}

/** 带路由跳转的无权页内容（须在 Router 内使用） */
export function ThemedAccessDeniedNavigate({
  theme,
  to,
  detail,
  backLabel,
}: DeniedProps & { to: string }) {
  const navigate = useNavigate()
  return (
    <ThemedAccessDeniedState
      theme={theme}
      detail={detail}
      backLabel={backLabel}
      onGoBack={() => navigate(to, { replace: true })}
    />
  )
}
